import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Typography,
  Chip,
  Skeleton,
  Grid,
  IconButton,
  useTheme,
  Fade,
  Container,
  Stack,
  Avatar,
} from "@mui/material";
import {
  Add,
  Edit,
  Pets,
  School,
  Height,
  Cake,
  Badge,
  CheckCircle,
  PhotoCamera,
} from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";
import EditModal from "./Modal/component";
import dayjs from "dayjs";

export type Dog = {
  name: string;
  details: DogDetails[];
};

export type DogDetails = {
  account_id: number;
  date_of_birth?: string;
  grade: number;
  height: string;
  id?: number;
  kc_name?: string;
  kc_number?: string;
  name: string;
  picture_url?: string;
};

const Dogs = (): JSX.Element => {
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [activeDog, setActiveDog] = useState<Dog>({} as Dog);
  const [modalOpen, setModalOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    getDogs();
  }, []);

  const getDogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");
      const response = await axios.get("https://api.easyagility.co.uk/dogs", {
        headers: { Authorization: "Bearer " + token },
      });

      setDogs(response.data);
    } catch (err) {
      setError("Failed to load dogs. Please try again later.");
      console.error("Error fetching dogs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index: number) => {
    setActiveDog(dogs[index]);
    setModalOpen(true);
  };

  const addDog = () => {
    const dog = {} as Dog;
    dog.details = [{} as DogDetails];

    setActiveDog(dog);
    setModalOpen(true);
  };

  const getHeightLabel = (height: string) => {
    const heightMap: { [key: string]: string } = {
      "200/300": "Any Size",
      "300": "Small",
      "400": "Medium",
      "500": "Intermediate",
      "600": "Large",
    };
    return heightMap[height] || height;
  };

  const getHeightColor = (height: string) => {
    const colorMap: {
      [key: string]:
        | "default"
        | "primary"
        | "secondary"
        | "success"
        | "warning"
        | "error";
    } = {
      "200/300": "default",
      "300": "primary",
      "400": "secondary",
      "500": "success",
      "600": "warning",
    };
    return colorMap[height] || "default";
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  const calculateAge = (dateOfBirth?: string) => {
    if (!dateOfBirth) return null;
    const birthDate = dayjs(dateOfBirth);
    const today = dayjs();
    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate, "month") % 12;

    if (years > 0) {
      return `${years} year${years > 1 ? "s" : ""}${
        months > 0 ? `, ${months} month${months > 1 ? "s" : ""}` : ""
      }`;
    } else {
      return `${months} month${months > 1 ? "s" : ""}`;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="60%" height={48} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: 300 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={32} />
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={120}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 1,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          My Dogs
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: "1rem", md: "1.1rem" },
            mb: 3,
          }}
        >
          Manage your dog's information and competition details
        </Typography>
      </Box>

      {/* Add Dog Button */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addDog}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontSize: { xs: "0.9rem", md: "1rem" },
            backgroundColor: theme.palette.success.main,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
            },
          }}
        >
          Add New Dog
        </Button>
      </Box>

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={getDogs}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Dogs Grid */}
      {dogs?.length === 0 ? (
        <Card
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: theme.shadows[2],
          }}
        >
          <Pets
            sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }}
          />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1,
              fontWeight: 500,
            }}
          >
            No Dogs Added Yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.disabled,
              mb: 3,
            }}
          >
            Add your first dog to get started with agility competitions.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={addDog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.success.main,
              "&:hover": {
                backgroundColor: theme.palette.success.dark,
              },
            }}
          >
            Add Your First Dog
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {dogs?.map((dog: Dog, index: number) => {
            const details = dog?.details?.find(
              (d) =>
                d.id === Math.max(...dog.details.map((obj) => obj.id || 0)),
            );
            const age = calculateAge(details?.date_of_birth);

            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={300}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: theme.shadows[2],
                      border: `1px solid ${theme.palette.divider}`,
                      transition: "all 0.3s ease-in-out",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: theme.shadows[8],
                      },
                    }}
                  >
                    {/* Dog Picture Section */}
                    <Box
                      sx={{
                        position: "relative",
                        height: 200,
                        backgroundColor: theme.palette.grey[100],
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {details?.picture_url ? (
                        <img
                          src={details.picture_url}
                          alt={`${dog.name}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            backgroundColor: theme.palette.primary.main,
                            fontSize: "2rem",
                          }}
                        >
                          <Pets sx={{ fontSize: 40 }} />
                        </Avatar>
                      )}
                      <IconButton
                        onClick={() => handleEdit(index)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.primary.dark,
                          },
                        }}
                        aria-label={`Edit ${dog.name}'s picture`}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Box>

                    <CardContent
                      sx={{
                        p: 3,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            lineHeight: 1.3,
                            flex: 1,
                            mr: 1,
                          }}
                        >
                          {dog.name}
                        </Typography>
                        <IconButton
                          onClick={() => handleEdit(index)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.light + "20",
                            },
                          }}
                          aria-label={`Edit ${dog.name}'s details`}
                        >
                          <Edit />
                        </IconButton>
                      </Box>

                      {/* Dog Details */}
                      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                        {/* Grade */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <School
                            sx={{
                              fontSize: 18,
                              color: theme.palette.text.secondary,
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                            }}
                          >
                            Grade {details?.grade || "Not set"}
                          </Typography>
                        </Box>

                        {/* Height */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Height
                            sx={{
                              fontSize: 18,
                              color: theme.palette.text.secondary,
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              mr: 1,
                            }}
                          >
                            Height:
                          </Typography>
                          <Chip
                            label={getHeightLabel(details?.height || "Not set")}
                            color={getHeightColor(details?.height || "")}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>

                        {/* Date of Birth */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Cake
                            sx={{
                              fontSize: 18,
                              color: theme.palette.text.secondary,
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {formatDate(details?.date_of_birth)}
                            {age && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.disabled,
                                  ml: 1,
                                }}
                              >
                                ({age})
                              </Typography>
                            )}
                          </Typography>
                        </Box>

                        {/* KC Information */}
                        {(details?.kc_name || details?.kc_number) && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Badge
                              sx={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                                mr: 1,
                              }}
                            />
                            <Box>
                              {details?.kc_name && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                  }}
                                >
                                  {details.kc_name}
                                </Typography>
                              )}
                              {details?.kc_number && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.disabled,
                                  }}
                                >
                                  KC: {details.kc_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>

                    {/* Action Button - Outside CardContent for better visibility */}
                    <Box
                      sx={{
                        p: 3,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                      }}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleEdit(index)}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          py: 1.5,
                          "&:hover": {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.light + "10",
                          },
                        }}
                      >
                        Edit Details
                      </Button>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Modal */}
      <EditModal
        getDogs={getDogs}
        open={modalOpen}
        setOpen={setModalOpen}
        dog={activeDog}
        setSuccessOpen={setSuccessOpen}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          icon={<CheckCircle />}
          sx={{
            width: "100%",
            borderRadius: 2,
            "& .MuiAlert-message": {
              fontSize: { xs: "0.9rem", md: "1rem" },
            },
          }}
        >
          Your dog's details have been successfully updated.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Dogs;
